import { type Group, type Organization, Status } from "@entities";
import { type ColumnProps } from "@shared/frontend/hyper-table/types";
import { MenuItem, Select, Stack } from "@mui/material";
import PhoneNumberCell from "../PhoneNumberCell";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { groupRepo } from "@repositories";
import { formatOpportunitySchedule } from "@shared/helpers/format-opportunity-schedule";

export const useGroupColumns = (currOrganization: Organization, setAlertText: any, setSeverity: any): ColumnProps<Group>[] => {
  return [
    {
      field: "name",
      enableSorting: true,
      minSize: 250
    },
    {
      field: "admin",
      minSize: 250,
      cell: ({ row }) => (
        <Stack alignItems="flex-start" flexDirection="column">
          <div>
            <strong>שם:</strong> {row.original.admin?.name}
          </div>
          <div>
            <strong>מספר טלפון:</strong>
            {" "}
            <PhoneNumberCell phoneNumber={row.original.admin?.phoneNumber || ""} />
          </div>
          <div>
            <strong>מייל:</strong> {row.original.admin?.email}
          </div>
        </Stack>
      ),
    },
    { field: "size" },
    {
      field: "preferredStartDate",
      cell: ({ row }) => {
        const { preferredStartDate } = row.original;
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
            adapterLocale="he"
          >
            <div className="flex gap-2" style={{ flexDirection: "row", display: "flex" }}>
              <DatePicker
                value={preferredStartDate ? dayjs(preferredStartDate) : null}
                onChange={async (start) => {
                  try {
                    await groupRepo.update(row.original.id, {
                      preferredStartDate: start ? dayjs(start).startOf("day").toDate() : undefined,
                    });
                    setAlertText("תאריך התחלה עודכן בהצלחה");
                    setSeverity("success");
                  } catch (e) {
                    setAlertText("שגיאה בעדכון תאריך התחלה");
                    setSeverity("error");
                  }
                }}
                reduceAnimations
                className="group-preferred-date-picker"
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    placeholder: 'תאריך התחלה',  // 👈 set your placeholder here
                    label: '',                 // 👈 hide the label
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        );
      },
      minSize: 200,
      preventRowClick: true,
    },
    {
      field: "preferredEndDate",
      cell: ({ row }) => {
        const { preferredEndDate } = row.original;
        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
            adapterLocale="he"
          >
            <div className="flex gap-2" style={{ flexDirection: "row", display: "flex" }}>
              <DatePicker
                value={preferredEndDate ? dayjs(preferredEndDate) : null}
                onChange={async (end) => {
                  try {
                    await groupRepo.update(row.original.id, {
                      preferredEndDate: end ? dayjs(end).startOf("day").toDate() : undefined,
                    });
                    setAlertText("תאריך סיום עודכן בהצלחה");
                    setSeverity("success");
                  } catch (e) {
                    setAlertText("שגיאה בעדכון תאריך סיום");
                    setSeverity("error");
                  }
                }}
                reduceAnimations
                className="group-preferred-date-picker"
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    placeholder: 'תאריך סיום',  // 👈 set your placeholder here
                    label: '',                 // 👈 hide the label
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        );
      },
      minSize: 200,
      preventRowClick: true,
    },
    {
      field: "opportunities",
      cell: ({ row }) => (
        <Stack>
          {row.original.opportunities?.map((event) => (
            <div key={event.opportunity?.id}>
              {!event.opportunity
                ? ""
                : `${event.opportunity.title} | ${formatOpportunitySchedule(event.opportunity)}`}
            </div>
          ))}
        </Stack>
      ),
    },
    { field: "comments" },
  ];
};